import { createApiRef } from '@backstage/frontend-plugin-api';

// Wire-contract DTOs (RunSummary, RunDetail, session types, etc.) live in
// `@internal/plugin-make-review-common` so this frontend client and the
// MCP actions backend module share one definition instead of hand-maintaining
// separate copies. Re-exported here so existing `'../api'` imports keep
// working unchanged.
export type {
  AgentSessionSummary,
  AnalyticsBucket,
  AnalyticsOverview,
  AnalyticsSnapshot,
  AxisLevel,
  ClassifierAxes,
  ClassifyResult,
  Confidence,
  DepthBucket,
  GetAnalyticsOverviewParams,
  GetRunsParams,
  GreenlightEvaluation,
  GreenlightEvaluationPolicy,
  GreenlightEvaluationStatus,
  GreenlightPolicy,
  GreenlightPolicyDefinition,
  GreenlightPolicyDefinitionUnavailableReason,
  GreenlightPolicySource,
  GreenlightRequirement,
  GreenlightRule,
  GreenlightRun,
  PiSessionEntryView,
  ReviewComment,
  ReviewerFailure,
  ReviewerInitialContext,
  ReviewRunDecision,
  ReviewerRun,
  ReviewerToolCallDiagnostic,
  ReviewerToolDiagnostics,
  ReviewerToolDisabledReason,
  ReviewRunResult,
  ReviewSessionView,
  RunDetail,
  RunStatus,
  RunSummary,
  RunsResponse,
  RunUsage,
  SessionContentBlock,
  SessionEntryKind,
  SessionsResponse,
  SessionStatus,
  SeverityTier,
} from '@internal/plugin-make-review-common';
import type {
  AgentSessionSummary,
  AnalyticsSnapshot,
  GetAnalyticsOverviewParams,
  GetRunsParams,
  GreenlightEvaluation,
  ReviewSessionView,
  RunDetail,
  RunsResponse,
} from '@internal/plugin-make-review-common';

export type MakeReviewApi = {
  /**
   * Fetches a fresh overview through the Backstage backend, which also shares
   * it with other viewers. Rejects when upstream is unavailable.
   */
  getAnalyticsOverview(
    params: GetAnalyticsOverviewParams,
  ): Promise<AnalyticsSnapshot>;
  /**
   * Reads the latest shared snapshot for exactly this scope without any
   * upstream work. Resolves null on a miss; the read is time-bounded and
   * stops when `options.signal` aborts.
   */
  getCachedAnalyticsOverview(
    params: GetAnalyticsOverviewParams,
    options?: { signal?: AbortSignal },
  ): Promise<AnalyticsSnapshot | null>;
  getRuns(params: GetRunsParams): Promise<RunsResponse>;
  /** Fetch a single run's detail. Throws NotFoundError when absent. */
  getRun(runId: string): Promise<RunDetail>;
  /**
   * Fetch a run's Greenlight evaluation joined to its pinned policy
   * definitions. Resolves null when the run has no Greenlight attempt.
   */
  getGreenlightEvaluation(runId: string): Promise<GreenlightEvaluation | null>;
  /** List all captured agent sessions for a run (metadata only). */
  listSessions(runId: string): Promise<AgentSessionSummary[]>;
  /** Fetch the parsed detail view for one session. */
  getSession(runId: string, sessionId: string): Promise<ReviewSessionView>;
  /** Download the raw Pi session JSONL as text (proxy injects auth). */
  downloadSessionRaw(runId: string, sessionId: string): Promise<string>;
  /** Establish cookie authentication and resolve the sandboxed preview URL. */
  getSessionPreviewUrl(
    runId: string,
    sessionId: string,
    theme: 'light' | 'dark',
    search: string,
  ): Promise<string>;
};

export const makeReviewApiRef = createApiRef<MakeReviewApi>().with({
  id: 'plugin.make-review.api',
});
